import { useState } from "react";
import { Minus, Plus, Trash2, Lock, Gift } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { api, formatError, money } from "../../lib/api";

export const CartDrawer = () => {
  const { items, update, remove, subtotal, open, setOpen, gift, setGift } = useCart();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const g = (k, v) => setGift({ ...gift, [k]: v });
  const giftInvalid = gift.enabled && (!gift.recipient_name.trim() || (!user && !(gift.sender_name || "").trim()) || (gift.notify_recipient && !/^\S+@\S+\.\S+$/.test(gift.recipient_email)));

  const checkout = async () => {
    setLoading(true);
    try {
      const { enabled, ...rest } = gift;
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
        gift: enabled ? { ...rest, sender_name: user ? user.name : (rest.sender_name || "").trim(), recipient_email: rest.recipient_email.trim() || null } : null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatError(e));
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" className="w-full sm:max-w-md flex flex-col bg-[#F8F7F4]">
        <SheetHeader><SheetTitle className="font-display text-2xl">Your cart</SheetTitle></SheetHeader>
        <div className="flex-1 overflow-y-auto mt-6 space-y-4">
          {items.length === 0 && <p data-testid="cart-empty" className="text-sm text-slate-500">Your cart is empty.</p>}
          {items.map((i) => (
            <div key={i.product_id} data-testid={`cart-item-${i.product_id}`} className="flex gap-3 bg-white rounded-xl border border-[#E4DFD4] p-3">
              <img src={i.image} alt={i.name} className="w-20 h-20 object-cover rounded-lg bg-[#EFECE5]" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold leading-snug line-clamp-2">{i.name}</div>
                <div className="font-mono text-sm text-[#0F766E] mt-1">{money(i.price)}</div>
                <div className="flex items-center gap-2 mt-2">
                  <button data-testid={`cart-dec-${i.product_id}`} onClick={() => update(i.product_id, i.quantity - 1)} className="p-1 rounded border border-[#E4DFD4] hover:bg-[#EFECE5]"><Minus className="w-3 h-3" /></button>
                  <span data-testid={`cart-qty-${i.product_id}`} className="font-mono text-sm w-6 text-center">{i.quantity}</span>
                  <button data-testid={`cart-inc-${i.product_id}`} onClick={() => update(i.product_id, i.quantity + 1)} className="p-1 rounded border border-[#E4DFD4] hover:bg-[#EFECE5]"><Plus className="w-3 h-3" /></button>
                  <button data-testid={`cart-remove-${i.product_id}`} onClick={() => remove(i.product_id)} className="ml-auto p-1 text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
        {items.length > 0 && (
          <div className="border-t border-[#E4DFD4] pt-4 space-y-3">
            <div className="flex justify-between text-sm"><span>Subtotal</span><span data-testid="cart-subtotal" className="font-mono font-bold">{money(subtotal)}</span></div>
            <div className="rounded-xl border border-[#E4DFD4] bg-white p-3">
              <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                <input type="checkbox" data-testid="cart-gift-toggle" className="w-4 h-4 accent-[#0D9488]" checked={gift.enabled} onChange={(e) => g("enabled", e.target.checked)} />
                <Gift className="w-4 h-4 text-[#0D9488]" /> This is a gift
              </label>
              {gift.enabled && (
                <div className="mt-3 space-y-2" data-testid="cart-gift-fields">
                  {!user && <input data-testid="cart-gift-sender-name" className="field" placeholder="From (your name)" maxLength={80} value={gift.sender_name || ""} onChange={(e) => g("sender_name", e.target.value)} />}
                  <input data-testid="cart-gift-recipient-name" className="field" placeholder="Recipient's name" maxLength={80} value={gift.recipient_name} onChange={(e) => g("recipient_name", e.target.value)} />
                  <textarea data-testid="cart-gift-message" className="field min-h-[70px]" placeholder="Gift message (optional)" maxLength={500} value={gift.message} onChange={(e) => g("message", e.target.value)} />
                  <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                    <input type="checkbox" data-testid="cart-gift-notify" className="accent-[#0D9488]" checked={gift.notify_recipient} onChange={(e) => g("notify_recipient", e.target.checked)} />
                    Email the recipient that a gift is on its way (with my message)
                  </label>
                  {gift.notify_recipient && <input data-testid="cart-gift-recipient-email" type="email" className="field" placeholder="Recipient's email" value={gift.recipient_email} onChange={(e) => g("recipient_email", e.target.value)} />}
                  <p className="text-xs text-slate-500">Enter the recipient's shipping address at checkout. Your receipt comes to you; prices are never shared with the recipient.</p>
                </div>
              )}
            </div>
            <p className="text-xs text-slate-500">Shipping address and any applicable tax are collected at secure checkout. A receipt is emailed as soon as payment completes.</p>
            <button data-testid="cart-checkout-btn" disabled={loading || giftInvalid} onClick={checkout} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0D9488] hover:bg-[#0F766E] disabled:opacity-60 text-white py-3.5 font-semibold transition-colors">
              <Lock className="w-4 h-4" /> {loading ? "Redirecting…" : "Secure checkout"}
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
